
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import * as data from "@/lib/wordpress/data";
import { configStore, uiStore } from "@/store/store";
import { ElementPreset } from "@/types/presets";
import defensiveCopy from "@/util/defensive-copy";
import { ControlsTreeContext } from "@/types/controls";
import WPMediaInput from "@/components/Reusable/CustomInputs/MediaInput/WPMediaInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import Dialog from "@/components/UI/Dialog.vue";
import { triggerDownload } from "@/util/trigger-download";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import { Media } from "@/types/media";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";

@Component({
  components: { WPMediaInput, TextInput, Dialog }
})
export default class SavePreset extends Vue {
  newPresetName: string | undefined | null = "";
  newPresetScreenshot: Media | null = null;

  @Prop()
  open!: boolean;

  @Inject("context") readonly context!: ControlsTreeContext;

  saveInProgress = false;

  async saveToLibrary(newPreset: ElementPreset) {
    this.saveInProgress = true;
    const newPresets = defensiveCopy(configStore.presetsForElements);
    newPresets.push(newPreset);

    pipe(
      await data.saveToWP("presets_for_elements", newPresets),
      fold(
        errors => {
          errors.forEach(e => NotificationLogger.log.message(e.msg));
        },
        () => {
          configStore.setPresetsForElements(newPresets);
        }
      )
    );

    this.saveInProgress = false;
  }

  makePreset() {
    const activeElement = uiStore.activeElement;
    if (!activeElement) return false;
    if (!this.newPresetName || !this.newPresetScreenshot) {
      NotificationLogger.log.message(
        "Presets must have a name and a screenshot."
      );
      return false;
    }

    const designProps = defensiveCopy(this.context.getControlValue("design"));

    if (!designProps) {
      NotificationLogger.log.message(
        "Unable to save blank design settings as preset."
      );
      return false;
    }

    const newPreset: ElementPreset = {
      availableForType: activeElement.data.type,
      designPropertiesData: designProps,
      label: this.newPresetName,
      screenshotUrl: this.newPresetScreenshot
    };

    return newPreset;
  }

  savePreset() {
    const newPreset = this.makePreset();
    if (newPreset) {
      this.saveToLibrary(newPreset);
      this.clearAndClose();
    }
  }

  exportPreset() {
    const newPreset = this.makePreset();
    if (newPreset) {
      triggerDownload(jsonStringifyOrUndefined(newPreset) || "", "preset.json");
      this.clearAndClose();
    }
  }

  clearAndClose() {
    this.newPresetName = "";
    this.newPresetScreenshot = null;
    this.$emit("update:dialog", false);
  }
}
